import usePrint from "@/shared/hooks/usePrint";
import usePrintReceipt from "@/shared/hooks/usePrintReceipt";
import useOrderSessionReceiptQuery from "@/shared/queries/orderssessions/useOrderSessionReceiptQuery";
import { cn } from "@/shared/utils/cn";
import { errorToast } from "@/shared/utils/helpers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon, { IconsVariant } from "../Icon/Icon";
import Spinner from "../Spinner/Spinner";

export type PrintType = "order" | "label" | "client";

const printOptions: { type: PrintType; icon: IconsVariant }[] = [
  { type: "order", icon: "receipt" },
  { type: "label", icon: "tag" },
  { type: "client", icon: "user" },
];

interface PrintActionsProps {
  orderId?: number;
  branchId?: number;
  sessionId?: number;
  types?: PrintType[];
  onPrint?: () => void;
  className?: string;
}

const PrintActions = ({
  orderId,
  branchId,
  sessionId,
  types = ["order", "label", "client"],
  onPrint,
  className,
}: PrintActionsProps) => {
  const { t } = useTranslation("", { keyPrefix: "printActions" });
  const printReceipt = usePrintReceipt();
  const printSessionReceipt = useOrderSessionReceiptQuery();
  const print = usePrint();
  const [pendingType, setPendingType] = useState<PrintType>(null);
  const isSession = Boolean(sessionId);

  const visibleOptions = printOptions.filter(
    ({ type }) => types.includes(type) && !(isSession && type === "label")
  );

  const printOrder = (isLabel: number, isCustomerReceipt: number) =>
    printReceipt(orderId, branchId, isLabel, isCustomerReceipt).catch(() => {});

  const printSession = (isCustomerReceipt: number) =>
    printSessionReceipt
      .mutateAsync({ sessionId, isCustomerReceipt })
      .then((data) => print(data))
      .catch(() => errorToast());

  const printByType: Record<PrintType, () => Promise<unknown>> = {
    order: () => (isSession ? printSession(0) : printOrder(0, 0)),
    label: () => printOrder(1, 0),
    client: () => (isSession ? printSession(1) : printOrder(0, 1)),
  };

  const handlePrint = (type: PrintType) => {
    setPendingType(type);
    printByType[type]()
      .then(() => onPrint?.())
      .finally(() => setPendingType(null));
  };

  if (!visibleOptions.length) return null;

  return (
    <div
      className={cn(
        "hidden md:block rounded-2xl border border-grey-blue-light bg-grey-secondary p-4",
        className
      )}
    >
      <div className="mb-2 flex items-center gap-2 text-btn-small font-semibold uppercase tracking-wider text-text-tertiary">
        <Icon variants="printer" className="size-5 shrink-0 fill-current" />
        {t("title")}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {visibleOptions.map(({ type, icon }) => (
          <button
            key={type}
            type="button"
            disabled={Boolean(pendingType)}
            onClick={() => handlePrint(type)}
            className={cn(
              "flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl border border-grey-blue-light bg-white px-5 py-4 text-left transition-colors duration-200",
              "hover:border-primary focus-visible:border-primary focus-visible:outline-none",
              pendingType && "cursor-not-allowed",
              pendingType && pendingType !== type && "opacity-60"
            )}
          >
            {pendingType === type ? (
              <Spinner size="0.2rem" className="shrink-0" />
            ) : (
              <Icon variants={icon} className="size-7 shrink-0 fill-primary" />
            )}
            <span className="min-w-0 flex-1 truncate text-default font-semibold leading-tight text-text">
              {t(type)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default PrintActions;
